/**
 * /ndt-inspection/welders/:id — one welder and their qualifications
 * (GET /ndt-inspection/welders/:id, POST /welders/:id/quals,
 * PATCH / DELETE /welder-quals/:id).
 *
 * Each qualification shows the API's two standings — expiry and process
 * continuity (lapses six months after the welder last used the process) — and
 * the overall one. The words never cross: "Expired" is a past expiry day,
 * "Lapsed" a past continuity day (ndtQualSkin, the same vocabulary as the
 * register and the Overview). The continuity day reads "Lapsed <day>" once it
 * is behind the tenant's today (`meta.today`) and "Lapses <day>" while ahead.
 * Diameter and wall ranges arrive in the tenant's length unit
 * (`meta.units.length`) and are printed as returned.
 *
 * The quals register is a table from `xl` and stacked cards below it, so at a
 * 1366px laptop with the sidebar open nothing is cut off at the right edge,
 * and at 390px nothing overflows.
 */
import React, { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { HardHat, Pencil, Plus, ShieldCheck, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { PageHeaderBand } from '../../components/layout/PageHeaderBand';
import { LoadFailureNotice } from '../../components/ui/LoadFailureNotice';
import { useAuth } from '../../contexts/AuthContext';
import { useDeleteNdtQual, useDeleteNdtWelder, useNdtWelder } from '../../hooks/useNdtInspection';
import { queryFailureError, queryLoadFailure } from '../../lib/load-failure';
import { canAdminNdt, formatNdtRange, ndtErrorMessage, ndtExpirySkin, ndtQualSkin } from '../../lib/ndt-inspection';
import type { NdtWelderQual } from '../../types/ndt-inspection';
import { ConfirmDialog, QualDialog, WelderDialog } from './NdtDialogs';
import { isNotFound, NDT_SECONDARY, NdtBadge, NdtEmpty, NdtLoading, NdtPanel, NdtSpec, ndtDay } from './NdtBits';
import { welderSkin } from './WeldersScreen';
import { NdtRepairRateCard } from './NdtJobBits';

/** "Lapsed Sep 3, 2026" once the day is behind the tenant's today; "Lapses …" while ahead (or today unknown). */
export function continuityPhrase(lapsesOn: string | null | undefined, today: string | null | undefined): string {
  if (!lapsesOn) return '—';
  return `${today && lapsesOn < today ? 'Lapsed' : 'Lapses'} ${ndtDay(lapsesOn)}`;
}

export default function WelderDetailScreen() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const query = useNdtWelder(id);
  const removeWelder = useDeleteNdtWelder();
  const removeQual = useDeleteNdtQual();
  const [editing, setEditing] = useState(false);
  const [qualOpen, setQualOpen] = useState(false);
  const [qualEditing, setQualEditing] = useState<NdtWelderQual | null>(null);
  const [confirm, setConfirm] = useState<{ kind: 'welder' } | { kind: 'qual'; qual: NdtWelderQual } | null>(null);
  const [confirmError, setConfirmError] = useState<string | null>(null);

  const welder = query.data?.data ?? null;
  const meta = query.data?.meta;
  const units = meta?.units ?? null;
  const failure = queryLoadFailure(query);
  const notFound = isNotFound(queryFailureError(query));
  const canEdit = canAdminNdt(user?.role) && !!welder;
  const quals = welder?.quals ?? [];

  const qualActions = (q: NdtWelderQual) => (
    <>
      <button type="button" className={NDT_SECONDARY} aria-label={`Edit ${q.codeLabel || q.code} ${q.process}`} onClick={() => { setQualEditing(q); setQualOpen(true); }} disabled={!units}>
        <Pencil className="size-4" aria-hidden="true" />
      </button>{' '}
      <button type="button" className={NDT_SECONDARY} aria-label={`Delete ${q.codeLabel || q.code} ${q.process}`} onClick={() => { setConfirmError(null); setConfirm({ kind: 'qual', qual: q }); }}>
        <Trash2 className="size-4" aria-hidden="true" />
      </button>
    </>
  );

  const doConfirm = () => {
    if (!confirm || !welder) return;
    setConfirmError(null);
    if (confirm.kind === 'welder') {
      removeWelder.mutate(welder.id, {
        onSuccess: () => {
          toast.success('Welder deleted');
          navigate('/ndt-inspection/welders', { replace: true });
        },
        onError: (err) => setConfirmError(ndtErrorMessage(err, 'The welder could not be deleted.')),
      });
    } else {
      removeQual.mutate(confirm.qual.id, {
        onSuccess: () => {
          toast.success('Qualification deleted');
          setConfirm(null);
        },
        onError: (err) => setConfirmError(ndtErrorMessage(err, 'The qualification could not be deleted.')),
      });
    }
  };

  return (
    <div data-testid="ndt-welder-detail-page" className="animate-fade-in min-h-full bg-fieldiq-dark">
      <PageHeaderBand
        eyebrow="NDT inspection · Welder"
        title={welder?.name || (query.isLoading ? 'Welder' : '—')}
        onBack={() => navigate(-1)}
        backLabel="Back"
        actions={
          canEdit ? (
            <button type="button" onClick={() => { setQualEditing(null); setQualOpen(true); }} className="fi-action fi-action--standard fi-action--primary" disabled={!units}>
              <Plus className="size-4" aria-hidden="true" />
              Add qualification
            </button>
          ) : undefined
        }
        stats={[
          {
            icon: <ShieldCheck className="size-4" aria-hidden="true" />,
            label: 'Standing',
            tone: welder?.qualStatus === 'valid' ? 'success' : welder?.qualStatus ? 'accent' : 'neutral',
            loading: query.isLoading,
            value: welder ? welderSkin(welder).label : '—',
          },
          {
            icon: <HardHat className="size-4" aria-hidden="true" />,
            label: 'Current',
            sub: 'Not expired or lapsed',
            tone: 'neutral',
            loading: query.isLoading,
            value: welder ? `${welder.currentQualCount} of ${welder.qualCount}` : '—',
          },
        ]}
      />

      <section className="px-[var(--fi-page-inline)] py-[var(--fi-page-block)] pb-[var(--bottom-chrome)]">
        <div className="mx-auto max-w-[1920px] space-y-4">
          {query.isLoading ? (
            <NdtLoading label="Loading welder" />
          ) : notFound || (!failure && !welder) ? (
            <NdtPanel eyebrow="Welder" title="Welder">
              <NdtEmpty>This welder was not found. They may have been deleted.</NdtEmpty>
            </NdtPanel>
          ) : failure || !welder ? (
            <NdtPanel eyebrow="Welder" title="Welder">
              <LoadFailureNotice error={queryFailureError(query)} kind={failure ?? 'unreachable'} subject="this welder" />
            </NdtPanel>
          ) : (
            <>
              <NdtPanel
                eyebrow="Welder"
                title="Details"
                actions={
                  canEdit ? (
                    <button type="button" className={NDT_SECONDARY} onClick={() => setEditing(true)}>
                      <Pencil className="size-4" aria-hidden="true" />
                      Edit
                    </button>
                  ) : undefined
                }
              >
                <dl className="grid gap-4 px-4 py-4 sm:grid-cols-2 lg:grid-cols-4">
                  <NdtSpec label="Name" value={welder.name || '—'} />
                  <NdtSpec label="Stamp" value={welder.welderStamp || '—'} mono />
                  <NdtSpec label="Employer" value={welder.employer || '—'} />
                  <NdtSpec label="FieldIQ user" value={welder.userName || '—'} />
                </dl>
                {welder.notes && <p className="border-t border-fieldiq-border px-4 py-3 text-sm text-fieldiq-text">{welder.notes}</p>}
              </NdtPanel>

              <NdtRepairRateCard welderId={welder.id} />

              <NdtPanel
                eyebrow="Qualifications"
                title="Welding procedure quals"
                testId="ndt-qual-list"
                footer={quals.length > 0 ? `${quals.length} qualification${quals.length === 1 ? '' : 's'}` : undefined}
              >
                {quals.length === 0 ? (
                  <NdtEmpty>This welder holds no qualification on file.</NdtEmpty>
                ) : (
                  <>
                    <div className="hidden overflow-x-auto xl:block" data-testid="ndt-qual-table">
                      <table className="w-full text-left">
                        <thead className="border-b border-fieldiq-border bg-fieldiq-dark/40 text-[0.64rem] font-black uppercase tracking-[0.12em] text-fieldiq-muted">
                          <tr className="border-l-4 border-l-transparent">
                            <th scope="col" className="px-4 py-3">Code</th>
                            <th scope="col" className="px-4 py-3">Process / position</th>
                            <th scope="col" className="px-4 py-3">Range{units?.length ? ` (${units.length})` : ''}</th>
                            <th scope="col" className="px-4 py-3">Qualified</th>
                            <th scope="col" className="px-4 py-3">Expiry</th>
                            <th scope="col" className="px-4 py-3">Continuity</th>
                            <th scope="col" className="px-4 py-3">Standing</th>
                            {canEdit && <th scope="col" className="px-4 py-3" />}
                          </tr>
                        </thead>
                        <tbody>
                          {quals.map((q) => (
                            <tr key={q.id} data-testid="ndt-qual-row" className={`border-t border-l-4 border-fieldiq-border ${ndtQualSkin(q.status).rail}`}>
                              <td className="px-4 py-3 text-sm font-extrabold text-white">{q.codeLabel || q.code}</td>
                              <td className="px-4 py-3 text-sm text-fieldiq-text">
                                {q.processLabel || q.process}
                                <p className="text-xs text-fieldiq-muted">{[q.position, q.progression].filter(Boolean).join(' · ') || '—'}</p>
                              </td>
                              <td className="whitespace-nowrap px-4 py-3 font-mono text-sm text-fieldiq-text">
                                <span className="text-xs text-fieldiq-muted">Dia</span> {formatNdtRange(q.diameterMin, q.diameterMax, units?.length)}
                                <p><span className="text-xs text-fieldiq-muted">Wall</span> {formatNdtRange(q.wallMin, q.wallMax, units?.length)}</p>
                              </td>
                              <td className="whitespace-nowrap px-4 py-3 text-sm text-fieldiq-text">{ndtDay(q.qualifiedOn)}</td>
                              <td className="whitespace-nowrap px-4 py-3 text-sm text-fieldiq-text">
                                {ndtDay(q.expiresOn)}
                                <div className="mt-1"><NdtBadge skin={ndtExpirySkin(q.expiryStatus)} value={q.expiryStatus} /></div>
                              </td>
                              <td className="whitespace-nowrap px-4 py-3 text-sm text-fieldiq-text">
                                <span data-testid="ndt-qual-continuity">{continuityPhrase(q.continuityLapsesOn, meta?.today)}</span>
                                <div className="mt-1"><NdtBadge skin={ndtQualSkin(q.continuityStatus)} value={q.continuityStatus} /></div>
                              </td>
                              <td className="px-4 py-3" data-testid="ndt-qual-standing"><NdtBadge skin={ndtQualSkin(q.status)} value={q.status} /></td>
                              {canEdit && <td className="whitespace-nowrap px-4 py-3 text-right">{qualActions(q)}</td>}
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                    <ul className="divide-y divide-fieldiq-border xl:hidden" data-testid="ndt-qual-cards">
                      {quals.map((q) => (
                        <li key={q.id} data-testid="ndt-qual-card" className={`border-l-4 px-4 py-3 ${ndtQualSkin(q.status).rail}`}>
                          <div className="flex flex-wrap items-start justify-between gap-2">
                            <div className="min-w-0">
                              <p className="text-sm font-extrabold text-white">{q.codeLabel || q.code} · {q.processLabel || q.process}</p>
                              <p className="text-xs text-fieldiq-muted">{[q.position, q.progression].filter(Boolean).join(' · ') || '—'}</p>
                            </div>
                            <NdtBadge skin={ndtQualSkin(q.status)} value={q.status} />
                          </div>
                          <dl className="mt-3 grid grid-cols-1 gap-3 text-sm sm:grid-cols-2">
                            <NdtSpec label={`Diameter${units?.length ? ` (${units.length})` : ''}`} value={formatNdtRange(q.diameterMin, q.diameterMax, units?.length)} mono />
                            <NdtSpec label={`Wall${units?.length ? ` (${units.length})` : ''}`} value={formatNdtRange(q.wallMin, q.wallMax, units?.length)} mono />
                            <NdtSpec label="Qualified" value={ndtDay(q.qualifiedOn)} />
                            <div>
                              <dt className="text-[0.62rem] font-black uppercase tracking-[0.12em] text-fieldiq-muted">Expiry</dt>
                              <dd className="mt-1 flex flex-wrap items-center gap-2 text-sm font-semibold text-white">{ndtDay(q.expiresOn)} <NdtBadge skin={ndtExpirySkin(q.expiryStatus)} value={q.expiryStatus} /></dd>
                            </div>
                            <div className="sm:col-span-2">
                              <dt className="text-[0.62rem] font-black uppercase tracking-[0.12em] text-fieldiq-muted">Continuity</dt>
                              <dd className="mt-1 flex flex-wrap items-center gap-2 text-sm font-semibold text-white">{continuityPhrase(q.continuityLapsesOn, meta?.today)} <NdtBadge skin={ndtQualSkin(q.continuityStatus)} value={q.continuityStatus} /></dd>
                            </div>
                          </dl>
                          {canEdit && <div className="mt-3 flex justify-end gap-2">{qualActions(q)}</div>}
                        </li>
                      ))}
                    </ul>
                  </>
                )}
              </NdtPanel>

              {canEdit && (
                <div className="flex justify-end">
                  <button type="button" className={NDT_SECONDARY} onClick={() => { setConfirmError(null); setConfirm({ kind: 'welder' }); }}>
                    <Trash2 className="size-4" aria-hidden="true" />
                    Delete welder
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      </section>

      {welder && canEdit && <WelderDialog open={editing} onClose={() => setEditing(false)} welder={welder} />}
      {welder && canEdit && (
        <QualDialog open={qualOpen} onClose={() => setQualOpen(false)} welderId={welder.id} qual={qualEditing} units={units} />
      )}
      {welder && canEdit && (
        <ConfirmDialog
          open={!!confirm}
          title={confirm?.kind === 'qual' ? 'Delete qualification' : 'Delete welder'}
          body={
            confirm?.kind === 'qual'
              ? `Remove the ${confirm.qual.codeLabel || confirm.qual.code} ${confirm.qual.processLabel || confirm.qual.process} qualification?`
              : `Remove ${welder.name} from the welder register?`
          }
          confirmLabel="Delete"
          pending={removeWelder.isPending || removeQual.isPending}
          error={confirmError}
          onClose={() => setConfirm(null)}
          onConfirm={doConfirm}
        />
      )}
    </div>
  );
}
